import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Sun, Moon, BookOpen, Image, Video, Tally4,
    MessageCircle, Music, Music2, Info, ArrowUpRight, Menu, X
} from 'lucide-react';
import { SocialIcon } from 'react-social-icons';
import { MediaProvider } from '@/context/MediaContext';

// StyleX Imports
import * as stylex from '@stylexjs/stylex';
import { layoutStyles } from '@/styles/Layout.stylex';
import { footerStyles } from '@/styles/Footer.stylex';

interface NavigationItem {
    title: string
    href?: string
    icon?: React.ReactNode
    className?: string
    children?: NavigationItem[]
}


// Theme Toggle Component
const ThemeToggle: React.FC<{
    isMobile?: boolean;
}> = ({ isMobile = false }) => {
    const [isDarkMode, setIsDarkMode] = useState(true);

    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            const isDark = savedTheme === 'dark';
            setIsDarkMode(isDark);
            document.documentElement.classList.toggle('light', !isDark);
            document.documentElement.classList.toggle('dark', isDark);
            document.body.classList.toggle('light', !isDark);
            document.body.classList.toggle('dark', isDark);
        } else {
            // Default to dark mode
            localStorage.setItem('theme', 'dark');
            document.documentElement.classList.add('dark');
            document.documentElement.classList.remove('light');
            document.body.classList.add('dark');
            document.body.classList.remove('light');
        }
    }, []);

    const toggleTheme = () => {
        const newTheme = isDarkMode ? 'light' : 'dark';
        setIsDarkMode(!isDarkMode);
        localStorage.setItem('theme', newTheme);

        // Update document classes
        if (newTheme === 'dark') {
            document.documentElement.classList.add('dark');
            document.documentElement.classList.remove('light');
            document.body.classList.add('dark');
            document.body.classList.remove('light');
        } else {
            document.documentElement.classList.add('light');
            document.documentElement.classList.remove('dark');
            document.body.classList.add('light');
            document.body.classList.remove('dark');
        }
    };

    return (
        <button
            onClick={toggleTheme}
            {...stylex.props(isMobile ? layoutStyles.mobileThemeToggle : layoutStyles.themeToggle)}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
            {isDarkMode ? (
                <Sun size={20} />
            ) : (
                <Moon size={20} />
            )}
        </button>
    );
};

// Header Component
const Header: React.FC<{
    onSidebarToggle: () => void;
    sidebarToggleRef: React.RefObject<HTMLButtonElement | null>;
}> = ({ onSidebarToggle, sidebarToggleRef }) => {
    return (
        <header {...stylex.props(layoutStyles.header)}>
            <div {...stylex.props(layoutStyles.headerContent)}>
                {/* Mobile Theme Toggle */}
                <div {...stylex.props(layoutStyles.mobileActions)}>
                    <button
                        ref={sidebarToggleRef}
                        {...stylex.props(layoutStyles.sidebarToggle)}
                        onClick={onSidebarToggle}
                        aria-label="Toggle sidebar">
                        <Menu size={20} />
                    </button>
                </div>
                {/* Logo */}
                <Link to="/" {...stylex.props(layoutStyles.logo)}>
                    <span {...stylex.props(layoutStyles.logoAccent)}>AiBrush</span>Documentation
                    <span {...stylex.props(layoutStyles.logoVersion)}>v3-Beta2</span>
                </Link>

                {/* Desktop Actions */}
                <div {...stylex.props(layoutStyles.desktopActions)}>
                    <a href="https://aibrush.co/workspace" target="_blank" rel="noopener noreferrer" {...stylex.props(layoutStyles.navLink)}>
                        Go to your workspace <ArrowUpRight size={16} />
                    </a>
                    <ThemeToggle />
                </div>

                {/* Mobile Theme Toggle */}
                <div {...stylex.props(layoutStyles.mobileActions)}>
                    <ThemeToggle isMobile />
                </div>
            </div>
        </header>
    );
};

const getSectionIconStyle = (className: string) => {
    switch (className) {
        case 'getting-started': return layoutStyles.iconGettingStarted;
        case 'core-features': return layoutStyles.iconCoreFeatures;
        case 'image-models': return layoutStyles.iconImageModels;
        case 'video-models': return layoutStyles.iconVideoModels;
        case 'speech-models': return layoutStyles.iconSpeechModels;
        case 'music-models': return layoutStyles.iconMusicModels;
        case 'sound-effects-models': return layoutStyles.iconSoundEffects;
        case 'chat-models': return layoutStyles.iconChatModels;
        case 'support': return layoutStyles.iconSupport;
        default: return null;
    }
};

export default function RootLayout({
    children,
}: {
    children: React.ReactNode
}) {
    const location = useLocation();
    const pathname = location.pathname;
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const sidebarRef = useRef<HTMLElement>(null);
    const sidebarToggleRef = useRef<HTMLButtonElement>(null);
    const starsContainerRef = useRef<HTMLDivElement>(null);
    const shapesRef = useRef<HTMLDivElement>(null);

    // Ensure theme is properly applied on mount
    useEffect(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'light') {
            document.documentElement.classList.add('light');
            document.documentElement.classList.remove('dark');
            document.body.classList.remove('dark');
            document.body.classList.add('light');
        } else {
            // Default to dark mode
            document.documentElement.classList.add('dark');
            document.documentElement.classList.remove('light');
            document.body.classList.add('dark');
            document.body.classList.remove('light');
        }
    }, []);

    // Handle sidebar toggle
    const handleSidebarToggle = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Handle sidebar close on escape key and click outside
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isSidebarOpen) {
                setIsSidebarOpen(false);
            }
        };

        const handleClickOutside = (e: MouseEvent) => {
            if (isSidebarOpen && sidebarRef.current && sidebarToggleRef.current) {
                // Check if click is outside sidebar and not on toggle button
                if (!sidebarRef.current.contains(e.target as Node) && !sidebarToggleRef.current.contains(e.target as Node)) {
                    setIsSidebarOpen(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isSidebarOpen]);

    // Stars data for React rendering
    const [stars, setStars] = useState<{ top: string; left: string; delay: string; size: string; color: string }[]>([]);

    useEffect(() => {
        // Generate stars only on client
        const newStars = [];
        for (let i = 0; i < 200; i++) {
            const size = 1 + Math.random() * 2;
            const hue = 190 + Math.random() * 60;
            newStars.push({
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                delay: `${Math.random() * 10}s`,
                size: `${size}px`,
                color: `hsl(${hue}, 100%, 70%)`
            });
        }
        setStars(newStars);

        // Add mouse parallax effect
        const handleMouseMove = (e: MouseEvent) => {
            const x = e.clientX / window.innerWidth;
            const y = e.clientY / window.innerHeight;

            // Calculate center-based coordinates (-1 to 1 range)
            const centerX = (x - 0.5) * 2;
            const centerY = (y - 0.5) * 2;

            // Stars with parallax effect
            if (starsContainerRef.current) {
                starsContainerRef.current.style.transform = `translate(${centerX * 10}px, ${centerY * 10}px)`;
            }

            // Geometric shapes with varied movement
            if (shapesRef.current) {
                shapesRef.current.style.transform = `translate(${centerX * 15}px, ${centerY * 15}px)`;
            }
        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    // Lock body scroll when sidebar is open on mobile
    useEffect(() => {
        if (isSidebarOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isSidebarOpen]);

    return (
        <MediaProvider>
            {/* Animated Background */}
            <div {...stylex.props(layoutStyles.animatedBackground)}>
                <div ref={starsContainerRef} className="stars-container" style={{ position: 'absolute', width: '100%', height: '100%' }}>
                    {stars.map((star, i) => (
                        <div
                            key={i}
                            {...stylex.props(layoutStyles.star)}
                            style={{
                                top: star.top,
                                left: star.left,
                                width: star.size,
                                height: star.size,
                                backgroundColor: star.color,
                                animationDelay: star.delay
                            } as React.CSSProperties}
                        />
                    ))}
                </div>
                <div ref={shapesRef} {...stylex.props(layoutStyles.geometricShapes)}>
                    <div {...stylex.props(layoutStyles.triangle)} style={{ position: 'absolute' }}></div>
                </div>
            </div>

            <div id="root" {...stylex.props(layoutStyles.docsLayout)}>
                <Header
                    onSidebarToggle={handleSidebarToggle}
                    sidebarToggleRef={sidebarToggleRef}
                />
                {/* Main Content */}
                <main {...stylex.props(layoutStyles.docsMain)}>
                    {/* Sidebar Overlay */}
                    <div
                        {...stylex.props(
                            layoutStyles.sidebarOverlay,
                            isSidebarOpen && layoutStyles.sidebarOverlayOpen
                        )}
                        onClick={() => setIsSidebarOpen(false)}
                        aria-hidden="true"
                    />

                    {/* Sidebar */}
                    <aside
                        ref={sidebarRef}
                        {...stylex.props(layoutStyles.sidebar, isSidebarOpen && layoutStyles.sidebarOpen)}
                    >
                        <button
                            onClick={() => setIsSidebarOpen(false)}
                            {...stylex.props(layoutStyles.sidebarClose)}
                            aria-label="Close sidebar"
                        >
                            <X size={20} />
                        </button>
                        <div style={{ padding: '1rem' }}>
                            {([
                                {
                                    title: 'Getting Started',
                                    icon: <BookOpen size={16} />,
                                    className: 'getting-started',
                                    children: [
                                        { title: 'Quick Start', href: '/quick-start' },
                                        { title: 'Examples', href: '/examples' },
                                    ]
                                },
                                {
                                    title: 'Core Features',
                                    icon: <Tally4 size={16} />,
                                    className: 'core-features',
                                    children: [
                                        { title: 'Workspace', href: '/workspace' },
                                        { title: 'Timeline Editor', href: '/timeline' },
                                        { title: 'AI Models', href: '/ai-models' },
                                        { title: 'ControlNet', href: '/controlnet' },
                                    ]
                                },
                                {
                                    title: 'Image Models',
                                    icon: <Image size={16} />,
                                    className: 'image-models',
                                    children: [
                                        { title: 'Google Imagen', href: '/images-models/google-imagen' },
                                        { title: 'Qwen Images', href: '/images-models/qwen-image' },
                                        { title: 'Runway Images', href: '/images-models/runway-image' },
                                        { title: 'Flux Pro', href: '/images-models/flux-pro' },
                                        { title: 'Wan Images', href: '/images-models/wan-image' },
                                        { title: 'See Dream', href: '/images-models/see-dream4' },
                                    ]
                                },
                                {
                                    title: 'Video Models',
                                    icon: <Video size={16} />,
                                    className: 'video-models',
                                    children: [
                                        { title: 'Google Veo', href: '/videos-models/google-veo' },
                                        { title: 'Runway Video', href: '/videos-models/runway-video' },
                                        { title: 'Pika', href: '/videos-models/pika-video' },
                                        { title: 'Hailuo', href: '/videos-models/hailuo-video' },
                                        { title: 'Wan Video', href: '/videos-models/wan-video' },
                                        { title: 'Pixverse Lipsync', href: '/videos-models/pixverse-lipsync' },
                                        { title: 'Veed Lipsync', href: '/videos-models/veed-lipsync' },
                                    ]
                                },
                                {
                                    title: 'Speech Models',
                                    icon: <MessageCircle size={16} />,
                                    className: 'speech-models',
                                    children: [
                                        { title: 'Gemini Speech', href: '/speech-models/gemini-speech' },
                                        { title: 'OpenAI Speech', href: '/speech-models/openai-speech' },
                                    ]
                                },
                                {
                                    title: 'Music Models',
                                    icon: <Music size={16} />,
                                    className: 'music-models',
                                    children: [
                                        { title: 'Suno', href: '/music-models/suno' },
                                        { title: 'Google Lyria', href: '/music-models/google-lyria' },
                                    ]
                                },
                                {
                                    title: 'Sound Effects Models',
                                    icon: <Music2 size={16} />,
                                    className: 'sound-effects-models',
                                    children: [
                                        { title: 'ElevenLabs Sound Effects', href: '/sound-effects-models/elevenlabs-sound-effects' },
                                    ]
                                },
                                {
                                    title: 'Support',
                                    icon: <Info size={16} />,
                                    className: 'support',
                                    children: [
                                        { title: 'Support', href: '/support' },
                                        { title: 'FAQ', href: '/faq' },
                                    ]
                                }
                            ] as NavigationItem[]).map((section, sectionIndex) => (
                                <div key={sectionIndex} style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: 'var(--text-tertiary)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        marginBottom: '0.75rem',
                                        paddingBottom: '0.5rem',
                                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        {section.icon && <span {...stylex.props(layoutStyles.sectionIcon, getSectionIconStyle(section.className || ''))}>{section.icon}</span>}
                                        {section.title}
                                    </h3>
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                        {section.children?.map((item, itemIndex) => (
                                            <li key={itemIndex}>
                                                <Link
                                                    to={item.href || '#'}
                                                    onClick={() => setIsSidebarOpen(false)}
                                                    {...stylex.props(
                                                        layoutStyles.sidebarNavLink,
                                                        pathname === item.href && layoutStyles.sidebarNavLinkActive
                                                    )}
                                                >
                                                    {item.title}
                                                </Link>

                                                {item.children && (
                                                    <div style={{ marginLeft: '1rem', marginTop: '0.25rem' }}>
                                                        {item.children.map((subItem, subIndex) => (
                                                            <Link
                                                                key={subIndex}
                                                                to={subItem.href || '#'}
                                                                onClick={() => setIsSidebarOpen(false)}
                                                                {...stylex.props(
                                                                    layoutStyles.sidebarNavLink,
                                                                    pathname === subItem.href && layoutStyles.sidebarNavLinkActive
                                                                )}
                                                                style={{ fontSize: '0.85rem' }}
                                                            >
                                                                {subItem.title}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </aside>

                    <div {...stylex.props(layoutStyles.docsContent)}>
                        {children}
                    </div>
                </main>

                <footer {...stylex.props(footerStyles.footer)}>
                    <div {...stylex.props(footerStyles.footerContent)}>
                        {/* Logo and About Section */}
                        <div {...stylex.props(footerStyles.footerSection)}>
                            <div {...stylex.props(footerStyles.logoSection)}>
                                <Link to="/" {...stylex.props(footerStyles.footerLogo)}>
                                    <span {...stylex.props(footerStyles.logoAccent)}>AiBrush</span>Documentation
                                </Link>
                                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>Create beautiful AI-powered media effortlessly.</p>
                            </div>
                        </div>

                        {/* Useful Links Section */}
                        <div {...stylex.props(footerStyles.footerSection)}>
                            <h3 {...stylex.props(footerStyles.footerTitle)}>Navigation</h3>
                            <Link to="/" {...stylex.props(footerStyles.footerLink)}>Home</Link>
                            <a href="https://aibrush.co" {...stylex.props(footerStyles.footerLink)} target="_blank" rel="noopener noreferrer"><span {...stylex.props(footerStyles.logoAccent)}>AiBrush </span></a>
                            <a href="https://aibrush.co/projects" {...stylex.props(footerStyles.footerLink)} target="_blank" rel="noopener noreferrer">Projects</a>
                            <a href="https://aibrush.co/workspace" {...stylex.props(footerStyles.footerLink)} target="_blank" rel="noopener noreferrer">Workspace</a>
                            <a href="https://aibrush.co/pricing" {...stylex.props(footerStyles.footerLink)} target="_blank" rel="noopener noreferrer">Pricing</a>
                        </div>

                        {/* Contact Information Section */}
                        <div {...stylex.props(footerStyles.footerSection)}>
                            <h3 {...stylex.props(footerStyles.footerTitle)}>Contact</h3>
                            <a href="mailto:support@aibrush.co" {...stylex.props(footerStyles.footerLink)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                                </svg>
                                support@aibrush.co
                            </a>

                            <h3 {...stylex.props(footerStyles.footerTitle)} style={{ marginTop: '1rem' }}>Social Media</h3>
                            <div {...stylex.props(footerStyles.socialLinks)}>
                                <div {...stylex.props(footerStyles.socialIcon)}>
                                    <SocialIcon
                                        url="https://github.com/aibrush"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ height: 36, width: 36 }}
                                        fgColor="currentColor"
                                        bgColor="transparent"
                                    />
                                </div>
                                <div {...stylex.props(footerStyles.socialIcon)}>
                                    <SocialIcon
                                        url="https://x.com/aibrushproject"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        network="x"
                                        style={{ height: 36, width: 36 }}
                                        fgColor="currentColor"
                                        bgColor="transparent"
                                    />
                                </div>
                                <div {...stylex.props(footerStyles.socialIcon)}>
                                    <SocialIcon
                                        url="https://www.instagram.com/aibrush.co"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ height: 36, width: 36 }}
                                        fgColor="currentColor"
                                        bgColor="transparent"
                                    />
                                </div>
                                <div {...stylex.props(footerStyles.socialIcon)}>
                                    <SocialIcon
                                        url="https://www.youtube.com/@aibrushproject"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        network="youtube"
                                        style={{ height: 36, width: 36 }}
                                        fgColor="currentColor"
                                        bgColor="transparent"
                                    />
                                </div>
                                <div {...stylex.props(footerStyles.socialIcon)}>
                                    <SocialIcon
                                        url="https://www.tiktok.com/@aibrushproject"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        network="tiktok"
                                        style={{ height: 36, width: 36 }}
                                        fgColor="currentColor"
                                        bgColor="transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Copyright Section */}
                    <div {...stylex.props(footerStyles.footerBottom)}>
                        <div {...stylex.props(footerStyles.copyright)}>
                            © {new Date().getFullYear()} AiBrush Studio. <span {...stylex.props(footerStyles.sparkle)}>✨</span> All rights reserved.
                        </div>
                        <div>
                            <a href="https://aibrush.co/coming-soon?page=privacy" {...stylex.props(footerStyles.footerLink)} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            {" • "}
                            <a href="https://aibrush.co/coming-soon?page=terms" {...stylex.props(footerStyles.footerLink)} target="_blank" rel="noopener noreferrer">Terms of Service</a>
                        </div>
                    </div>
                </footer>
            </div>
        </MediaProvider>
    )
}
